import { ArrowDown, Check, X } from "lucide-react";

const ROWS = [
  { label: "Complete clean energy stack, not just caffeine", values: ["yes", "some", "no", "no"] },
  { label: "Calm, sustained focus", values: ["yes", "yes", "no", "some"] },
  { label: "Mood & motivation", values: ["yes", "some", "no", "no"] },
  { label: "Blunts cortisol, builds stress resilience", values: ["yes", "some", "no", "no"] },
  { label: "Recovery, not just push-through", values: ["yes", "no", "no", "no"] },
  { label: "Crisp, flavor-forward taste — not earthy, grassy or bitter", values: ["yes", "no", "yes", "no"] },
  { label: "0g sugar, no sucralose", values: ["yes", "some", "no", "yes"] },
];

const CellMark = ({ value, testId }) => {
  if (value === "yes") return <Check size={17} strokeWidth={1.8} className="sample-accent" aria-label="Yes" data-testid={testId} />;
  if (value === "some") return <span className="label !text-[6.5px] !tracking-[0.16em] !text-white/50 sm:!text-[8px]" data-testid={testId}>Some</span>;
  return <X size={13} strokeWidth={1.4} className="text-white/22" aria-label="No" data-testid={testId} />;
};

export const FeedbackComparison = () => (
  <section className="bg-[linear-gradient(120deg,#07100b_0%,#07100b_68%,#0a2a1b_100%)] text-[#faf9f5]" data-testid="feedback-comparison-section">
    <div className="mx-auto max-w-site px-6 py-24 md:px-10 md:py-32">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="label !text-[10px] !text-white/48">A category of one</div>
        </div>
        <div className="md:col-span-8">
          <h2 className="font-display text-4xl leading-[1.02] sm:text-5xl" data-testid="feedback-comparison-title">
            All-day performance,<br /><span className="editorial sample-editorial-strong" data-testid="feedback-comparison-editorial">without sacrifices.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-white/68" data-testid="feedback-comparison-intro">
            Energy drinks pull one lever hard. Focus shots stop at the desk. Vitura is built for people training hard <span className="editorial text-lg text-white/85">and</span> thinking hard—fifteen ingredients covering the whole day, not one hour of it.
          </p>
        </div>
      </div>

      <div className="mt-14 border-t border-white/15" data-testid="feedback-comparison-table">
        <div className="grid min-h-16 grid-cols-[minmax(0,1.7fr)_repeat(4,minmax(0,.52fr))] items-end border-b border-white/15">
          <span className="pb-4 label !text-[7px] !text-white/40 sm:!text-[9px]">Capability</span>
          {[
            ["Vitura", "sample-accent"],
            ["Focus", ""],
            ["Energy", ""],
            ["Coffee", ""],
          ].map(([label, tone], index) => (
            <span key={label} className={`flex h-full items-end justify-center pb-4 text-center label !text-[7px] !tracking-[0.1em] !text-white/45 sm:!text-[9px] ${tone} ${index === 0 ? 'bg-[#103522]/70' : ''}`} data-testid={`feedback-comparison-column-${index}`}>
              {label}
            </span>
          ))}
        </div>

        {ROWS.map((row, rowIndex) => (
          <div key={row.label} className="grid min-h-[72px] grid-cols-[minmax(0,1.7fr)_repeat(4,minmax(0,.52fr))] border-b border-white/15" data-testid={`feedback-comparison-row-${rowIndex}`}>
            <div className="flex items-center pr-3 text-[12px] leading-tight text-white/82 sm:text-sm">{row.label}</div>
            {row.values.map((value, valueIndex) => (
              <div key={`${row.label}-${valueIndex}`} className={`flex items-center justify-center ${valueIndex === 0 ? 'bg-[#103522]/70' : ''}`}>
                <CellMark value={value} testId={`feedback-comparison-value-${rowIndex}-${valueIndex}`} />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div id="caffeine-comparison" tabIndex={-1} aria-label="Vitura versus your energy drink" className="grid scroll-mt-8 grid-cols-1 border-b border-white/15 md:grid-cols-2" data-testid="feedback-caffeine-comparison">
        <div className="border-b border-white/15 py-9 md:border-b-0 md:border-r md:pr-10">
          <div className="label !text-[8px] !text-white/42">Celsius · C4 · Bloom · Red Bull · Alani Nu</div>
          <div className="mt-4 flex items-baseline gap-2 font-display text-4xl text-white/60">
            200–250<span className="text-sm">mg caffeine</span>
          </div>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-white/52">
            Synthetic, plus sucralose or sugar. One lever, pulled hard—often inside a “proprietary blend” that hides the real doses.
          </p>
        </div>

        <div className="py-9 md:pl-10">
          <div className="sample-accent label !text-[8px]" data-testid="feedback-caffeine-vitura-label">Vitura · One 2oz shot</div>
          <div className="mt-4 flex items-baseline gap-2 font-display text-4xl">
            170<span className="text-sm">mg stimulant load <span className="font-mono text-[9px] text-white/42">(caffeine equiv.)</span></span>
          </div>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/72" data-testid="feedback-caffeine-vitura-copy">
            Enfinity® Paraxanthine + green tea, buffered by 200mg Suntheanine® L-Theanine to take the edge off. Underneath: Ashwagandha for stress, Cognizin® and Lion’s Mane for cognition, Affron® Saffron for mood and 8 more clinically-dosed, active ingredients — for the same daily price as a Celsius. Zero sugar. Zero sucralose. Zero crash.
          </p>
        </div>
      </div>

      <a href="#full-ingredient-stack" className="sample-accent mt-10 inline-flex items-center gap-3 border-b border-current pb-2 text-base transition-opacity duration-200 hover:opacity-70 focus-visible:outline focus-visible:outline-offset-4" data-testid="sample-full-stack-link">See the full ingredient stack <ArrowDown size={17} className="shrink-0" aria-hidden="true" /></a>
    </div>
  </section>
);